import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Pencil, Trash2, Shield, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getAdmins, createAdmin, updateAdmin, deleteAdmin } from '../../lib/api';

const AdminAdmins = () => {
  const { t, isRTL } = useLanguage();
  const { isSuperAdmin, user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'data_entry',
    name_ar: '',
    name_en: '',
    is_active: true,
    new_password: ''
  });

  const loadData = async () => {
    try {
      const res = await getAdmins();
      setAdmins(res.data);
    } catch (error) {
      toast.error(t('خطأ في تحميل البيانات', 'Error loading data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const openCreateDialog = () => {
    setSelectedAdmin(null);
    setFormData({
      username: '',
      password: '',
      role: 'data_entry',
      name_ar: '',
      name_en: '',
      is_active: true,
      new_password: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      username: admin.username,
      password: '',
      role: admin.role,
      name_ar: admin.name_ar || '',
      name_en: admin.name_en || '',
      is_active: admin.is_active,
      new_password: ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username) {
      toast.error(t('يرجى ملء اسم المستخدم', 'Please fill username'));
      return;
    }
    if (!selectedAdmin && !formData.password) {
      toast.error(t('يرجى إدخال كلمة المرور', 'Please enter password'));
      return;
    }

    setSubmitting(true);
    try {
      if (selectedAdmin) {
        await updateAdmin(selectedAdmin.id, {
          username: formData.username,
          role: formData.role,
          name_ar: formData.name_ar,
          name_en: formData.name_en,
          is_active: formData.is_active,
          new_password: formData.new_password || null
        });
        toast.success(t('تم تحديث المسؤول بنجاح', 'Admin updated successfully'));
      } else {
        await createAdmin({
          username: formData.username,
          password: formData.password,
          role: formData.role,
          name_ar: formData.name_ar,
          name_en: formData.name_en
        });
        toast.success(t('تم إنشاء المسؤول بنجاح', 'Admin created successfully'));
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || t('حدث خطأ', 'An error occurred');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAdmin(selectedAdmin.id);
      toast.success(t('تم حذف المسؤول بنجاح', 'Admin deleted successfully'));
      setDeleteDialogOpen(false);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || t('حدث خطأ أثناء الحذف', 'Error deleting admin');
      toast.error(message);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">{t('غير مصرح لك بالوصول إلى هذه الصفحة', 'You are not authorized to access this page')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-raad-rose border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-admins">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('إدارة المسؤولين', 'Admin Management')}</h1>
          <p className="text-gray-400">{t('إدارة حسابات المسؤولين والصلاحيات', 'Manage admin accounts and permissions')}</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-gradient-raad" data-testid="add-admin-btn">
          <Plus className="w-4 h-4 me-2" />
          {t('إضافة مسؤول', 'Add Admin')}
        </Button>
      </div>

      {/* Roles Legend */}
      <div className="mb-6 flex gap-6">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-raad-gold" />
          <span className="text-gray-400 text-sm">{t('مسؤول رئيسي: كل الصلاحيات', 'Super Admin: All permissions')}</span>
        </div>
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-blue-400" />
          <span className="text-gray-400 text-sm">{t('مدخل بيانات: إضافة وتعديل فقط', 'Data Entry: Add and edit only')}</span>
        </div>
      </div>

      {admins.length === 0 ? (
        <Card className="bg-raad-card border-white/10">
          <CardContent className="py-12 text-center">
            <p className="text-gray-400">{t('لا يوجد مسؤولين', 'No admins yet')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {admins.map((admin) => (
            <Card key={admin.id} className="bg-raad-card border-white/10">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${admin.role === 'super_admin' ? 'bg-raad-gold/20' : 'bg-blue-500/20'}`}>
                      {admin.role === 'super_admin' ? (
                        <Shield className="w-6 h-6 text-raad-gold" />
                      ) : (
                        <User className="w-6 h-6 text-blue-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{admin.name_ar || admin.name_en || admin.username}</h3>
                      <p className="text-gray-500 text-sm">@{admin.username}</p>
                    </div>
                  </div>
                  {admin.username !== 'admin' && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(admin)} className="text-gray-400 hover:text-white">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => { setSelectedAdmin(admin); setDeleteDialogOpen(true); }} className="text-gray-400 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs ${admin.role === 'super_admin' ? 'bg-raad-gold/20 text-raad-gold' : 'bg-blue-500/20 text-blue-400'}`}>
                    {admin.role === 'super_admin' ? t('مسؤول رئيسي', 'Super Admin') : t('مدخل بيانات', 'Data Entry')}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs ${admin.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'}`}>
                    {admin.is_active ? t('نشط', 'Active') : t('غير نشط', 'Inactive')}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-raad-card border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedAdmin ? t('تعديل المسؤول', 'Edit Admin') : t('إضافة مسؤول', 'Add Admin')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">{t('الاسم (عربي)', 'Name (Arabic)')}</Label>
                <Input
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  dir="rtl"
                />
              </div>
              <div>
                <Label className="text-gray-300">{t('الاسم (إنجليزي)', 'Name (English)')}</Label>
                <Input
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  dir="ltr"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">{t('اسم المستخدم', 'Username')} *</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-white/5 border-white/10 text-white mt-1"
                disabled={selectedAdmin?.username === 'admin'}
              />
            </div>
            {!selectedAdmin && (
              <div>
                <Label className="text-gray-300">{t('كلمة المرور', 'Password')} *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            )}
            {selectedAdmin && (
              <div>
                <Label className="text-gray-300">{t('كلمة مرور جديدة (اختياري)', 'New Password (optional)')}</Label>
                <Input
                  type="password"
                  value={formData.new_password}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  placeholder={t('اتركه فارغاً للإبقاء على كلمة المرور الحالية', 'Leave empty to keep current')}
                />
              </div>
            )}
            <div>
              <Label className="text-gray-300">{t('الصلاحية', 'Role')}</Label>
              <Select
                value={formData.role}
                onValueChange={(v) => setFormData({ ...formData, role: v })}
                disabled={selectedAdmin?.username === 'admin'}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-raad-card border-white/10">
                  <SelectItem value="super_admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-raad-gold" />
                      {t('مسؤول رئيسي', 'Super Admin')}
                    </div>
                  </SelectItem>
                  <SelectItem value="data_entry">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-400" />
                      {t('مدخل بيانات', 'Data Entry')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedAdmin && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(c) => setFormData({ ...formData, is_active: c })}
                  disabled={selectedAdmin?.username === 'admin'}
                />
                <Label className="text-gray-300">{t('نشط', 'Active')}</Label>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                {t('إلغاء', 'Cancel')}
              </Button>
              <Button type="submit" disabled={submitting} className="bg-gradient-raad">
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : selectedAdmin ? t('تحديث', 'Update') : t('إنشاء', 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-raad-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('تأكيد الحذف', 'Confirm Delete')}</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {t('هل أنت متأكد من حذف هذا المسؤول؟', 'Are you sure you want to delete this admin?')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              {t('إلغاء', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              {t('حذف', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminAdmins;
